import * as vp from 'vopay';
import * as Yup from 'yup';
import { FunctionComponent } from 'react';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useLayout } from '../../../providers/Layout.provider';
import { usePaylink } from '../../../providers/Paylink.provider';
import { buildCreditCardInfoRows, buildCreditCardInfoCodeRows } from './buildCreditCard.helper';
import { ConnectCreditCardHandler } from './types';

const { ElementContainer, Box, Term, TextField, Button, Icon } = vp;

type CreditCardFormProps = {
  defaultValues: vp.IQ11CreditCard;
  handlers: ConnectCreditCardHandler;
  buttonDisableState: boolean;
};

type FC = FunctionComponent<CreditCardFormProps>;

const CreditCardForm: FC = (props: CreditCardFormProps) => {
  const { color } = useLayout();
  const { Token } = usePaylink();
  const { defaultValues, handlers, buttonDisableState } = props;
  const CreditCardInfoRows = buildCreditCardInfoRows();
  const CreditCardInfoCodeRows = buildCreditCardInfoCodeRows();

  const { t } = useTranslation('translations', { keyPrefix: 'CreditCardForm' });
  return (
    <Formik
      initialValues={defaultValues}
      validationSchema={createdValidationSchema()}
      onSubmit={handlers.onConnectCreditCard}
    >
      {({ touched, errors, handleChange, handleBlur, handleSubmit }) => (
        <form onSubmit={handleSubmit}>
          {CreditCardInfoRows.map(({ label, id, required, preset, security }) => (
            <ElementContainer
              key={id}
              label={label}
              required={required}
              color="black"
              input={
                <TextField
                  name={id}
                  variant="background"
                  preset={preset}
                  security={security}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={errors[id] && touched[id] ? errors[id] : null}
                  elementRight={
                    preset === 'creditCard' ? <Icon icon="eyeOpen" color="primary-dark-transparent" size="sm" /> : <></>
                  }
                />
              }
            />
          ))}

          <Box gap="sm" width="100%" jc="sb" height="auto">
            {CreditCardInfoCodeRows.map(({ label, id, required, preset, date, placeholder, security }) => (
              <Box key={id} flx="col" width="49%" height="auto">
                <ElementContainer
                  label={label}
                  required={required}
                  color="black"
                  input={
                    <TextField
                      name={id}
                      variant="background"
                      preset={preset}
                      creditCard
                      security={security}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={errors[id] && touched[id] ? errors[id] : null}
                      placeholder={placeholder && placeholder}
                      elementRight={
                        date && !Token ? (
                          <Icon icon="dateCalendar" color="low-light" height="16px" />
                        ) : security ? (
                          <Icon icon="eyeOpen" color="primary-dark-transparent" size="sm" />
                        ) : (
                          <></>
                        )
                      }
                    />
                  }
                />
              </Box>
            ))}
          </Box>

          <Box mt="md" mb="lg" height="auto">
            <Term
              term={
                <div>
                  {' '}
                  {t('termsStart')}{' '}
                  <a href="https://vopay.com/legal/services/tos/" target="_blank" rel="noreferrer">
                    {t('termsLink')}
                  </a>{' '}
                  {t('termsMiddle')}{' '}
                  <a href="https://vopay.com/legal/services/privacy-policy/" target="_blank" rel="noreferrer">
                    {t('termsLink2')}
                  </a>
                  . {t('termsEnd')}
                </div>
              }
            />
          </Box>

          <Box height="auto">
            <Button
              id="credit-card-connection-btn"
              label={Token ? `${t('continueButton')}` : `${t('connectCardButton', { defaultValue: 'Connect Card' })}`}
              type="submit"
              background={color}
              textColor={vp.getContrastColor(color)}
              width="100%"
              size="xlg"
              round
              bold
              noShadow
              isDisabled={buttonDisableState}
              textSize={1}
            />
          </Box>
        </form>
      )}
    </Formik>
  );

  function createdValidationSchema() {
    return Yup.object().shape({
      FullName: Yup.string().required(`${t('nameError')}`),
      CardNumber: Yup.string()
        .test('test-number', `${t('cardNumberErrorInvalid')}`, (value) => {
          let digit;
          let n;
          let _ref2;
          let valid;
          let _j;
          let _len1;
          let sum = 0;

          value = value?.replace(/[ -]/g, '') || '';
          _ref2 = value?.split('').reverse() || '';

          for (n = _j = 0, _len1 = _ref2?.length; _j < _len1; n = ++_j) {
            digit = _ref2[n];
            digit = +digit;

            if (n % 2) {
              digit *= 2;

              if (digit < 10) {
                sum += digit;
              } else {
                sum += digit - 9;
              }
            } else {
              sum += digit;
            }
          }
          valid = sum % 10 === 0;

          return valid;
        })
        .min(15, `${t('cardNumberError')}`),
      ExpirationDate: Yup.string()
        .test('test-credit-card-expiration-date', `${t('expErrorPast')}`, (expirationDate) => {
          if (!expirationDate) {
            return false;
          }
          const today = new Date();
          const monthToday = today.getMonth() + 1;
          const yearToday = today.getFullYear().toString().substr(-2);
          const [expMonth, expYear] = expirationDate.split('/');

          if (Number(expYear) < Number(yearToday)) {
            return false;
          }
          if (Number(expMonth) < monthToday && Number(expYear) <= Number(yearToday)) {
            return false;
          }
          return true;
        })
        .min(4, `${t('expError')}`),
      CVV: Yup.string()
        .min(3, `${t('CVVError')}`)
        .max(4, `${t('CVVError')}`)
        .required(`${t('CVVError')}`),
      Address: Yup.string().required(`${t('streetAddressError')}`),
    });
  }
};

export default CreditCardForm;
